import * as React from 'react';

import * as Styled from './progress-badge-fractional.styles';
import type { ProgressBadgeFractionalProps } from './progress-badge-fractional.types';

export const ProgressBadgeFractional = React.forwardRef<
  HTMLSpanElement,
  ProgressBadgeFractionalProps
>(
  (
    { className, currentValue, totalValue, variant = 'default', ...props },
    forwardedRef
  ): JSX.Element | null => {
    // Sanitize input values, ensure non-negative numbers
    const sanitizedCurrentValue = React.useMemo(() => {
      if (currentValue === null || currentValue === undefined) return null;
      const num = Number.isFinite(currentValue) ? currentValue : 0;
      return Math.max(0, num);
    }, [currentValue]);

    const sanitizedTotalValue = React.useMemo(() => {
      if (totalValue === null || totalValue === undefined) return null;
      const num = Number.isFinite(totalValue) ? totalValue : 0;
      return Math.max(0, num);
    }, [totalValue]);

    // Precompute progress; treat invalid inputs as 0
    const progress = React.useMemo(() => {
      if (
        sanitizedCurrentValue === null ||
        sanitizedCurrentValue === undefined ||
        sanitizedTotalValue === null ||
        sanitizedTotalValue === undefined
      )
        return 0;

      if (sanitizedTotalValue === 0) return 0;
      const raw = (sanitizedCurrentValue / sanitizedTotalValue) * 100;
      if (!Number.isFinite(raw) || Number.isNaN(raw)) return 0;
      return Math.min(100, Math.max(0, raw));
    }, [sanitizedCurrentValue, sanitizedTotalValue]);

    // Allow zero values; only guard against null/undefined after computing progress
    if (
      sanitizedCurrentValue === null ||
      sanitizedCurrentValue === undefined ||
      sanitizedTotalValue === null ||
      sanitizedTotalValue === undefined
    ) {
      return null;
    }

    return (
      <Styled.ProgressBadgeFractional
        $progress={Math.round(progress)}
        $variant={variant}
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={Math.round(progress)}
        aria-valuetext={`${sanitizedCurrentValue}/${sanitizedTotalValue}`}
        className={className}
        data-xds="ProgressBadgeFractional"
        ref={forwardedRef}
        role="progressbar"
        {...props}
      >
        <Styled.Text
          forwardedAs="span"
          fontWeight="bold"
          noMargin
          size="xsmall"
        >
          {`${sanitizedCurrentValue}/${sanitizedTotalValue}`}
        </Styled.Text>
      </Styled.ProgressBadgeFractional>
    );
  }
);

ProgressBadgeFractional.displayName = 'ProgressBadgeFractional';
